import axios from 'axios'
import { BASE_URL_STRIPE } from './index'
import { ICartItem, ICoupon, IStullerAccountObj, ITax } from '../interface'
import { getTimeWithoutTimezone } from '../services'
import { Dispatch } from 'redux'
import { Cookies } from 'react-cookie'
import { notification } from '../components/NotificationEvents'
import { sortItemsForBizzabo } from '../utils/bizzabo'

export const GET_CART_PRODUCTS = 'GET_CART_PRODUCTS'
export const CART_IS_LOADING = 'CART_IS_LOADING'
export const CART_IS_NOT_LOADING = 'CART_IS_NOT_LOADING'
export const TAXES_TO_APPLY = 'TAXES_TO_APPLY'
export const CLEAN_CART = 'CLEAN_CART'

const cookies = new Cookies()

export const getCartFromDynamo = (customerId: string, taxes: ITax[] = [], coupon: ICoupon | null = null) => {
  if (!customerId) {
    // This is a workaround to avoid a nonsensical request when the customerId is undefined
    return
  }
  return async (dispatch: Dispatch) => {
    try {
      const request = await axios.get(`${BASE_URL_STRIPE}/cart`, {
        params: {
          customerId,
          country_code: cookies.get('country_code'),
          taxes: JSON.stringify(taxes),
          coupon: JSON.stringify(coupon),
          now: getTimeWithoutTimezone()
        }
      })
      request.data.Items = sortItemsForBizzabo(request.data.Items)
      dispatch({
        type: GET_CART_PRODUCTS,
        payload: request.data
      })
    } catch (error) {
      console.log('getCartFromDynamo', error)
      notification.sendNotification('error', 'notification.error')
    }
  }
}

export const getTaxAfterLogin = (datasOfUser: { Country: string; State: string; Zip: string }): any => {
  return async (dispatch: Dispatch) => {
    try {
      const request = await axios.get(`${BASE_URL_STRIPE}/tax`, {
        params: {
          Country: datasOfUser.Country,
          State: datasOfUser.State,
          Zip: datasOfUser.Zip
        }
      })

      dispatch({
        type: TAXES_TO_APPLY,
        payload: request
      })
      return request
    } catch (error: any) {
      console.log('Error on getTaxAfterLogin - ', error)
      throw new Error(error)
    }
  }
}

export const getCartFromDynamoAfterLogin = (customerId: string, taxes: ITax): any => {
  if (!customerId) {
    // This is a workaround to avoid a nonsensical request when the customerId is undefined
    return
  }
  return async (dispatch: Dispatch) => {
    try {
      const request = await axios.get(`${BASE_URL_STRIPE}/cart`, {
        params: {
          customerId,
          taxes: JSON.stringify(taxes),
          now: getTimeWithoutTimezone(),
          country_code: cookies.get('country_code')
        }
      })
      request.data.Items = sortItemsForBizzabo(request.data.Items)
      dispatch({
        type: GET_CART_PRODUCTS,
        payload: request.data
      })
    } catch (error: any) {
      console.error('Error on getCartFromDynamoAfterLogin - ', error)
      notification.sendNotification('error', 'notification.error')
    }
  }
}

export const updateCart = ({
  customerId,
  taxes = [],
  coupon = undefined,
  userCart,
  stullerAccountObj
}: {
  customerId: string
  taxes: ITax[]
  coupon: ICoupon | undefined
  userCart: ICartItem[]
  stullerAccountObj: IStullerAccountObj
}) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch({ type: CART_IS_LOADING })
      const request = await axios.put(`${BASE_URL_STRIPE}/cart`, {
        customerId,
        taxes,
        coupon,
        userCart,
        stullerAccountObj,
        now: getTimeWithoutTimezone(),
        country_code: cookies.get('country_code')
      })
      request.data.Items = sortItemsForBizzabo(request.data.Items)
      dispatch({
        type: GET_CART_PRODUCTS,
        payload: request.data
      })
      notification.sendNotification('success', 'notification.cartUpdated')
    } catch (error: any) {
      if (error?.response?.data?.name) {
        notification.sendNotification('error', error.response.data.name)
      } else if (error?.response?.data) {
        notification.sendNotification('error', error.response.data)
      } else {
        notification.sendNotification('error', 'notification.error')
        console.log('Error on updateCart - ', error)
      }
      dispatch({ type: CART_IS_NOT_LOADING })
    }
  }
}

export const postCart = ({
  taxes = [],
  coupon = undefined,
  userCart,
  countryCode = 'us'
}: {
  taxes: ITax[]
  coupon: ICoupon | undefined
  userCart: ICartItem[]
  countryCode?: string
}) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch({ type: CART_IS_LOADING })
      const request = await axios.post(`${BASE_URL_STRIPE}/cart`, {
        coupon,
        customerId: null,
        now: getTimeWithoutTimezone(),
        taxes,
        userCart: { products: userCart },
        country_code: countryCode?.toLowerCase()
      })
      request.data.Items = sortItemsForBizzabo(request.data.Items)
      dispatch({
        type: GET_CART_PRODUCTS,
        payload: request.data
      })
      notification.sendNotification('success', 'notification.cartUpdated')
    } catch (error: any) {
      if (error?.response?.data?.name) {
        notification.sendNotification('error', error.response.data.name)
      } else if (error?.response?.data) {
        notification.sendNotification('error', error.response.data)
      } else {
        notification.sendNotification('error', 'notification.error')
        console.log('Error on postCart - ', error)
      }
      dispatch({ type: CART_IS_NOT_LOADING })
    }
  }
}

export const deleteUserCart = (customerId: string) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch({ type: CART_IS_LOADING })
      await axios.delete(`${BASE_URL_STRIPE}/cart`, {
        params: {
          customerId
        }
      })
      dispatch({ type: CLEAN_CART })
      return Promise.resolve()
    } catch (error) {
      console.log('Error on deleteUserCart - ', error)
      dispatch({ type: CART_IS_NOT_LOADING })
      notification.sendNotification('error', 'notification.error')
    }
  }
}

export const cleanCart = () => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CLEAN_CART })
  }
}
